import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { Button, Divider, message, Modal, Space } from 'antd';

import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';

import {
  CreateCustomerAddressRequest,
  CustomerAddress,
  UpdateCustomerAddressRequest,
} from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import {
  useCreateCustomerAddress,
  useCustomerAddresses,
  useDeleteCustomerAddress,
  useUpdateCustomerAddress,
} from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import { PageProps } from '@/types/PageProps';

import AddressModal from '@/components/_common/AddressModal';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { ColumnsType } from '@/components/_common/Table';
import { AddressDefaultType } from '@/components/_common/AddressModal/types';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { getTableScrollX } from '@/utils/table';
import { InfiniteTable, SaveButton } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useCustomerFormContext } from '@/components/Customers/CustomerFormProvider';
import CustomerFooter from '@/components/Customers/CustomerRepeatedButtons';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useAddressColumns } from '@/hooks/useAddressColumns';
import { isAppPortocar } from '@/index';

const CustomerAddressesPage = ({ route }: PageProps) => {
  const { columns: addressColumns } = useAddressColumns<CustomerAddress>();
  const intl = useIntl();
  const { setParamLabel } = useBreadcrumbs();
  const { customerId } = useRouteMatch<{ customerId: string }>().params;
  const {
    handleSubmit,
    disabled,
    isLoading: contextLoad,
  } = useCustomerFormContext();

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress>();

  const { mutateAsync: getCustomer } = useCustomerReduced();

  useEffect(() => {
    setBreadcrumbLabel();
  }, [customerId]);

  const { data: addressesData, isLoading } = useCustomerAddresses(
    Number(customerId),
  );

  const createAddressMutation = useCreateCustomerAddress(Number(customerId));
  const updateAddressMutation = useUpdateCustomerAddress(Number(customerId));
  const deleteAddressMutation = useDeleteCustomerAddress(Number(customerId));

  const setBreadcrumbLabel = async () => {
    const customer = await getCustomer(Number(customerId));

    setParamLabel(':customerId', customer.name);
  };

  const handleModalVisible = async (address?: CustomerAddress) => {
    if (address) {
      await setSelectedAddress(address);
    } else {
      await setSelectedAddress(undefined);
    }

    setModalVisible(!modalVisible);
  };

  const handleModalOkClick = async (values: AddressDefaultType) => {
    if (values?.id) {
      const obj: UpdateCustomerAddressRequest = {
        ...values,
        main: !!values.main,
        delivery: !!values.delivery,
      };

      await updateAddressMutation.mutateAsync(obj);
    } else {
      const obj: CreateCustomerAddressRequest = {
        ...values,
        main: !!values.main,
        delivery: !!values.delivery,
      };

      await createAddressMutation.mutateAsync(obj);
    }
  };

  const handleRemoveClick = (address: CustomerAddress) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.address.remove',
      }),
      content: intl.formatMessage({
        id: 'customers.address.remove.alert',
      }),
      onOk: async () => {
        if (isAppPortocar && addressesData?.data.length === 1) {
          message.destroy();
          message.warning('É obrigatório ao menos um endereço cadastrado.');
          return;
        } else {
          await deleteAddressMutation.mutateAsync(address.id);
        }
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<CustomerAddress> = [
    ...addressColumns,
    {
      title: <FormattedMessage id="customers.list.header.actions" />,
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EditIcon iconProps={{ onClick: () => handleModalVisible(row) }} />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={customerId}
        basePath={`/app/customers/${customerId}`}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() => handleModalVisible()}
              disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_ADD])}
            >
              <FormattedMessage id="commons.entity.add" />
            </Button>
            <SaveButton
              block
              loading={contextLoad}
              onClick={handleSubmit}
              disabled={
                disabled || !hasPermissions([Authorities.REGISTER_CUSTOMER_ADD])
              }
            >
              {intl.formatMessage({ id: 'commons.entity.save' })}
            </SaveButton>
          </SpaceForButton>
        }
      >
        <InfiniteTable<CustomerAddress>
          columns={columns}
          dataSource={addressesData?.data}
          loading={isLoading}
          rowKey={(row) => row.id}
          pagination={false}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <CustomerFooter customerId={Number(customerId)} disabled={disabled} />

        <AddressModal
          visible={modalVisible}
          onClose={handleModalVisible}
          onOk={handleModalOkClick}
          address={selectedAddress}
          loading={
            createAddressMutation.isLoading || updateAddressMutation.isLoading
          }
          hasAddress={!!addressesData?.data.length}
          removeDelivery={false}
          removeDescription={false}
          removeMain={false}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default CustomerAddressesPage;
